import { AccessGroupsRelation } from "../../../models/access-groups";
import { AccessPointRelation } from "../../../models/access-points";
import { CompanyRelation } from "../../../models/company-model";
import { GateRelation } from "../../../models/gate-model";
import { PropertyRelation, PropertySettingsJson } from "../../../models/property-model";
import { StrapiUnitRelation } from "../../../models/units";
import { User, UserRelation } from "../../../models/user-model";
import { RequirementTypesRelation } from "../../../models/requirement-types";
import { CompanyData } from "../../../models/company-model";
import {
  AccessGroup,
  AccessGroupField,
  AccessPoint,
  ADMIN_UNIT_NAME,
  DEFAULT_PROPERTY,
  DEFAULT_PROPERTY_SETTINGS,
  Gate,
  GROUP_PHOTOS,
  GROUP_ROLES,
  GroupPhotoKey,
  GroupRoleKey,
  INVITE_ROLES,
  InviteRoleKey,
  LayoutPin,
  PropertyDetails,
  PropertySettings,
  WEEK_DAY_NAMES,
  WizardUnit,
  WizardUser,
  makeEmptyAccessPoint,
  makeEmptyGate,
} from "../types";
import { resolveStrapiMediaId, resolveStrapiMediaUrl } from "./strapiMedia";

export interface PropertySetupLoadedState {
  propertyDocumentId: string;
  propertyId: number;
  prop: PropertyDetails;
  selectedCompany: CompanyData | null;
  logoMediaId?: number;
  layoutMediaId?: number;
  accessPoints: AccessPoint[];
  groups: AccessGroup[];
  units: WizardUnit[];
  users: WizardUser[];
}

const asArray = <T>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const formatCoord = (value: number | null | undefined): string =>
  value == null || Number.isNaN(value) ? "" : String(value);

const parseTimeToHm = (value?: string | null): string => {
  if (!value?.trim()) return "08:00";
  const [hours = "08", minutes = "00"] = value.split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

const mergePropertySettings = (
  settings?: PropertySettingsJson | null,
): PropertySettings => ({
  ...DEFAULT_PROPERTY_SETTINGS,
  ...(settings as Partial<PropertySettings>),
});

const mapRoleTypeToGroupKey = (type?: string): GroupRoleKey | null => {
  if (!type) return null;
  const normalized = type.toLowerCase();
  for (const role of GROUP_ROLES) {
    if (
      normalized === role.roleTypePrefix ||
      normalized === `${role.roleTypePrefix}_b`
    ) {
      return role.key;
    }
  }
  return null;
};

const mapRoleTypeToInviteKey = (type?: string): InviteRoleKey => {
  if (!type) return "Usuario Principal";
  const normalized = type.toLowerCase();
  for (const role of INVITE_ROLES) {
    if (
      normalized === role.roleTypePrefix ||
      normalized === `${role.roleTypePrefix}_b`
    ) {
      return role.key;
    }
  }
  return "Usuario Principal";
};

const parsePhone = (phone?: string): {
  phoneCountryCode: string;
  phoneNumber: string;
} => {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (digits.startsWith("52") && digits.length >= 12) {
    return { phoneCountryCode: "+52", phoneNumber: digits.slice(2, 12) };
  }
  if (digits.startsWith("1") && digits.length >= 11) {
    return { phoneCountryCode: "+1", phoneNumber: digits.slice(1, 11) };
  }
  return {
    phoneCountryCode: "+52",
    phoneNumber: digits.slice(-10),
  };
};

const mapCompany = (company?: CompanyRelation | null): CompanyData | null => {
  if (!company?.id) return null;
  return {
    id: company.id,
    documentId: company.documentId,
    name: company.name ?? "",
    logo_url: company.logo_url ?? "",
    createdAt: company.createdAt ?? "",
    updatedAt: company.updatedAt ?? "",
    locale: company.locale ?? "",
    tax_id: company.tax_id ?? "",
    legal_name: company.legal_name ?? "",
    localizations: company.localizations ?? "",
  };
};

const mapGate = (gate: GateRelation): Gate => ({
  id: crypto.randomUUID(),
  strapiId: gate.id,
  documentId: gate.documentId,
  name: gate.name ?? "Puerta",
  type: gate.type === "exit" ? "Salida" : "Entrada",
});

const mapAccessPoints = (
  points: AccessPointRelation[],
  propertyLat: string,
  propertyLng: string,
): { accessPoints: AccessPoint[]; idByStrapiId: Record<number, string> } => {
  const idByStrapiId: Record<number, string> = {};
  const mapped =
    points.length > 0
      ? points.map((point) => {
          const localId = crypto.randomUUID();
          idByStrapiId[point.id] = localId;
          const gates = asArray(point.gates);
          return {
            id: localId,
            strapiId: point.id,
            documentId: point.documentId,
            nombre: point.name ?? "",
            calleOAvenida: point.vanue_name ?? "",
            longitud: formatCoord(point.longitude) || propertyLng,
            latitud: formatCoord(point.latitude) || propertyLat,
            zonaHoraria: point.time_zone_custom?.id
              ? String(point.time_zone_custom.id)
              : "",
            gates:
              gates.length > 0
                ? gates.map(mapGate)
                : [makeEmptyGate()],
          };
        })
      : [
          (() => {
            const empty = makeEmptyAccessPoint({
              lat: propertyLat,
              lng: propertyLng,
            });
            return empty;
          })(),
        ];

  return { accessPoints: mapped, idByStrapiId };
};

const mapLayoutPins = (
  accessPoints: AccessPoint[],
  strapiPoints: AccessPointRelation[],
): LayoutPin[] => {
  const localByStrapiId = new Map<number, string>();
  accessPoints.forEach((point) => {
    if (point.strapiId) {
      localByStrapiId.set(point.strapiId, point.id);
    }
  });

  return strapiPoints
    .filter((point) => point.layout_coordinates)
    .map((point) => {
      const localId = localByStrapiId.get(point.id);
      if (!localId || !point.layout_coordinates) return null;
      return {
        accessPointId: localId,
        xPercent: point.layout_coordinates.xPercent,
        yPercent: point.layout_coordinates.yPercent,
      };
    })
    .filter((pin): pin is LayoutPin => pin != null);
};

const mapRequirementField = (
  requirement: RequirementTypesRelation,
): AccessGroupField => ({
  id: crypto.randomUUID(),
  label: requirement.name ?? requirement.description ?? "",
  type: requirement.data_type === "selector" ? "selector" : "input",
  options: asArray(requirement.selector_data).join(", "),
  required: Boolean(requirement.required),
});

const mapAccessGroup = (
  group: AccessGroupsRelation,
  idByStrapiId: Record<number, string>,
): AccessGroup => {
  const config = group.configuration?.[0];
  const days = WEEK_DAY_NAMES.map((day) =>
    config?.week_days?.includes(day) ? 1 : 0,
  );
  const photos = GROUP_PHOTOS.reduce(
    (acc, photo) => {
      acc[photo.key] = config?.photos?.includes(photo.key) ?? false;
      return acc;
    },
    {} as Record<GroupPhotoKey, boolean>,
  );

  const roles = asArray(group.role_permissions_users)
    .map((role) => mapRoleTypeToGroupKey(role.type))
    .filter((key): key is GroupRoleKey => key != null);

  const points = asArray(group.access_points)
    .map((point) => idByStrapiId[point.id])
    .filter((id): id is string => Boolean(id));

  return {
    id: crypto.randomUUID(),
    strapiId: group.id,
    documentId: group.documentId,
    name: group.name ?? "Grupo",
    points,
    roles,
    photos,
    from: parseTimeToHm(config?.start_time),
    to: parseTimeToHm(config?.end_time),
    exitRestricted: Boolean(config?.exit_restriction),
    exitFrom: parseTimeToHm(config?.exit_restriction),
    exitTo: parseTimeToHm(config?.end_time),
    days,
    fields: asArray(group.requirement_types).map(mapRequirementField),
  };
};

const mapUnits = (units: StrapiUnitRelation[]): WizardUnit[] => {
  const mapped = units.map((unit) => ({
    id: crypto.randomUUID(),
    strapiId: unit.id,
    documentId: unit.documentId,
    name: unit.is_admin ? ADMIN_UNIT_NAME : unit.name ?? "Unidad",
    locked: Boolean(unit.is_admin),
  }));

  if (!mapped.some((unit) => unit.locked)) {
    return [
      {
        id: crypto.randomUUID(),
        name: ADMIN_UNIT_NAME,
        locked: true,
      },
      ...mapped,
    ];
  }

  return mapped;
};

const mapUsers = (
  users: (User | UserRelation)[],
  units: WizardUnit[],
  idByStrapiId: Record<number, string>,
): WizardUser[] =>
  users.map((user) => {
    const roleKey = mapRoleTypeToInviteKey(user.role?.type);
    const unitRecord = units.find(
      (unit) => unit.strapiId && user.unit?.id === unit.strapiId,
    );
    const unitName =
      roleKey === "Administración"
        ? ADMIN_UNIT_NAME
        : unitRecord?.name ?? user.unit?.name ?? "";
    const phone = parsePhone(user.phone);

    return {
      id: crypto.randomUUID(),
      strapiId: user.id,
      documentId: user.documentId,
      name: user.name ?? user.username ?? "",
      email: user.email ?? "",
      phoneCountryCode: phone.phoneCountryCode,
      phoneNumber: phone.phoneNumber,
      role: roleKey,
      unit: unitName,
      points: asArray(user.access_points)
        .map((point) => idByStrapiId[point.id])
        .filter((id): id is string => Boolean(id)),
      locked: false,
    };
  });

export function mapPropertyToWizardState(
  property: PropertyRelation,
  extras?: {
    units?: StrapiUnitRelation[];
    users?: (User | UserRelation)[];
    accessGroups?: AccessGroupsRelation[];
  },
): PropertySetupLoadedState {
  const meta = property.address_metadata ?? {};
  const propertyLat = formatCoord(property.latitude) || DEFAULT_PROPERTY.lat;
  const propertyLng = formatCoord(property.longitude) || DEFAULT_PROPERTY.lng;

  const prop: PropertyDetails = {
    ...DEFAULT_PROPERTY,
    name: property.name ?? "",
    street: meta.street ?? "",
    streetNumber: meta.streetNumber ?? "",
    colonia: meta.colonia ?? "",
    city: meta.city ?? "",
    state: meta.state ?? "",
    zip: meta.zip ?? "",
    address: property.address ?? "",
    type: property.type ?? "",
    tz: meta.timeZoneId ?? "",
    lat: propertyLat,
    lng: propertyLng,
    logo: resolveStrapiMediaUrl(property.image),
    layoutImage: resolveStrapiMediaUrl(property.layout),
    layoutPins: [],
    settings: mergePropertySettings(property.settings),
  };

  const strapiPoints = asArray(property.access_points);
  const { accessPoints, idByStrapiId } = mapAccessPoints(
    strapiPoints,
    propertyLat,
    propertyLng,
  );
  prop.layoutPins = mapLayoutPins(accessPoints, strapiPoints);

  const units = mapUnits(
    extras?.units?.length ? extras.units : asArray(property.units),
  );
  const unitIdByStrapiId: Record<number, string> = {};
  units.forEach((unit) => {
    if (unit.strapiId) {
      unitIdByStrapiId[unit.strapiId] = unit.id;
    }
  });

  const groupsSource =
    extras?.accessGroups?.length
      ? extras.accessGroups
      : asArray(property.access_groups);
  const groups = groupsSource.map((group) =>
    mapAccessGroup(group, idByStrapiId),
  );

  const usersSource =
    extras?.users?.length ? extras.users : asArray(property.users);
  const users = mapUsers(usersSource, units, idByStrapiId);

  return {
    propertyDocumentId: property.documentId,
    propertyId: property.id,
    prop,
    selectedCompany: mapCompany(property.company),
    logoMediaId: resolveStrapiMediaId(property.image),
    layoutMediaId: resolveStrapiMediaId(property.layout),
    accessPoints,
    groups,
    units,
    users,
  };
}
